import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react'

export interface CollapseProps {
  className?: string
  contentClassName?: string
  id?: string
  isOpen?: boolean
  /**
   * If a transition for the height is used,
   * set the duration of the transition here
   * to allow reset of overflow when the transition
   * is complete.
   */
  transitionDurationMs?: number
}

export const Collapse = ({
  children,
  className = '',
  contentClassName = '',
  id,
  isOpen,
  transitionDurationMs,
}: PropsWithChildren<CollapseProps>) => {
  const [transitionComplete, setTransitionComplete] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<NodeJS.Timeout>()

  const handleResize = useCallback(() => {
    const container = containerRef.current
    const content = contentRef.current

    if (container && content && isOpen) {
      container.style.height = `${content.clientHeight}px`
      if (transitionDurationMs) {
        if (timerRef?.current) {
          clearTimeout(timerRef.current)
        }
        timerRef.current = setTimeout(
          () => setTransitionComplete(true),
          transitionDurationMs
        )
      }
    }
  }, [isOpen, transitionDurationMs])

  useEffect(() => {
    window.addEventListener('resize', handleResize, { passive: true })
    return () => window.removeEventListener('resize', handleResize)
  }, [handleResize])

  useEffect(() => {
    const container = containerRef.current
    const content = contentRef.current

    if (container && content) {
      const containerHeight = container.clientHeight
      const contentHeight = content.clientHeight

      const isFullyClosed = !isOpen && containerHeight === 0
      const isFullyOpened = isOpen && containerHeight === contentHeight

      if (isFullyClosed || isFullyOpened) {
        container.style.overflow = isOpen ? 'initial' : 'hidden'
        container.style.height = isOpen ? 'auto' : '0px'
      } else {
        container.style.overflow = 'hidden'
        if (isOpen) {
          container.style.height = `${contentHeight}px`
        } else {
          container.style.height = '0px'
        }

        if (transitionDurationMs && isOpen) {
          if (timerRef?.current) {
            clearTimeout(timerRef.current)
          }
          timerRef.current = setTimeout(
            () => setTransitionComplete(true),
            transitionDurationMs
          )
        }
      }
    }
    return () => {
      if (timerRef?.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [isOpen, transitionDurationMs])

  useEffect(() => {
    if (transitionComplete && isOpen) {
      const container = containerRef.current
      if (container) {
        container.style.overflow = 'initial'
        container.style.height = 'auto'
      }

      setTransitionComplete(false)
    }
  }, [transitionComplete, isOpen])

  return (
    <div className={`h-0 overflow-hidden ${className}`} id={id} ref={containerRef}>
      <div className={contentClassName} ref={contentRef}>
        {children}
      </div>
    </div>
  )
}
